import { useMemo } from "react";
import { useField, useFormikContext } from "formik";
import classNames from "classnames";
import {
    SCHEDULE_POLICY_KIND_LABELS,
    SchedulePolicyKind,
} from "@common/helpers/domains/accounting/schedulePolicyKind";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerSectionData } from "@design-system/Picker/Picker.types";
import { Input } from "@design-system/Input/Input";
import { useScheduleHubScopes } from "./ScheduleHubScopeProvider";
import { ScheduleHubScope } from "./scheduleHubScope";
import { ScheduleGroupCombobox } from "./ScheduleGroupCombobox";
import { ScheduleTemplateFormValues } from "./scheduleFormHelpers";
import { changeSchedulePolicyKind } from "./schedulePolicyKindForm";
import styles from "./ScheduleDrawer.module.scss";

function toKindSection(
    scope: ScheduleHubScope,
): PickerSectionData<SchedulePolicyKind> {
    return {
        title: scope.pickerSectionTitle,
        items: scope.policyKinds.map((kind) => ({
            value: kind,
            label: SCHEDULE_POLICY_KIND_LABELS[kind],
        })),
    };
}

function InlineTextField({
    fieldName,
    label,
    placeholder,
}: {
    fieldName: "name" | "description";
    label: string;
    placeholder?: string;
}) {
    const { submitCount } = useFormikContext<ScheduleTemplateFormValues>();
    const [field, meta] = useField<string>(fieldName);
    const shouldShowError =
        meta.error != null && (meta.touched || submitCount > 0);

    return (
        <div className={classNames(styles.cardRow, styles.cardRowFlush)}>
            <span className={styles.inlineLabel}>{label}</span>
            <div className={styles.rowInput}>
                <Input
                    {...field}
                    className={styles.drawerInput}
                    placeholder={placeholder}
                    size="md"
                    invalid={shouldShowError}
                />
                {shouldShowError && (
                    <p className={styles.fieldError}>{meta.error}</p>
                )}
            </div>
        </div>
    );
}

export const ScheduleDetailsCard: React.FC = () => {
    const { values, setFieldValue, setValues } =
        useFormikContext<ScheduleTemplateFormValues>();
    const scheduleHubScopes = useScheduleHubScopes();
    const sections = useMemo(
        () => scheduleHubScopes.map(toKindSection),
        [scheduleHubScopes],
    );

    return (
        <div className={classNames(styles.card, styles.cardStack)}>
            <div className={classNames(styles.cardRow, styles.cardRowFlush)}>
                <span className={styles.inlineLabel}>Type</span>
                <Picker.Select
                    sections={sections}
                    value={values.kind}
                    onValueChange={(kind) => {
                        if (kind == null || kind === values.kind) {
                            return;
                        }
                        void setValues({
                            ...values,
                            ...changeSchedulePolicyKind(values, kind),
                        });
                    }}
                />
                <span
                    className={classNames(
                        styles.inlineLabel,
                        styles.inlineLabelGap,
                    )}
                >
                    Group
                </span>
                <ScheduleGroupCombobox
                    value={values.group}
                    onChange={(group) => setFieldValue("group", group)}
                />
            </div>
            <InlineTextField fieldName="name" label="Name" />
            <InlineTextField fieldName="description" label="Description" />
        </div>
    );
};
